import { useMutation, useQueryClient } from '@tanstack/react-query';
import { staffConfigModeQueryParams } from '~/hooks/analyticsStatAccess/statAccessQueryScope';
import { staffClientReportTemplatesAccountQueryKey } from '~/hooks/queries/useStaffClientReportTemplatesQuery';
import type { Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import { customFetch } from '~/utils/customFetch';
import {
  AnalyticsStatsApiError,
  flattenAnalyticsStatsErrorResponse,
} from '~/views/Dashboard/AnalyticsStats/analyticsStatsErrorUtils';
import { invalidateClientAnalyticsQueries } from './invalidateClientAnalyticsQueries';

export type AssignResult = {
  templatePublicId: string;
  status: 'assigned' | 'skipped' | 'failed';
  assignedPublicId?: string;
  code?: string;
  message?: string;
};

export type UnassignResult = {
  assignedPublicId: string;
  status: 'unassigned' | 'failed';
  code?: string;
  message?: string;
};

type AssignResponse = { results: AssignResult[] };
type UnassignResponse = { results: UnassignResult[] };

const assignStaffClientReportTemplates = async (params: {
  accountType: 'client' | 'partner';
  accountId: string;
  templatePublicIds: string[];
  analyticsReportingMode?: Genie2ReportingMode | null;
}): Promise<AssignResponse> => {
  const { accountType, accountId, templatePublicIds, analyticsReportingMode } = params;
  const { data, status } = await customFetch.post<unknown>(
    `/authenticated/staff/${accountType}/${accountId}/analytics/report-templates/assign`,
    JSON.stringify({ templatePublicIds }),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      params: staffConfigModeQueryParams(analyticsReportingMode),
    },
  );

  if (status >= 400) {
    throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
  }
  return data as AssignResponse;
};

const unassignStaffClientReportTemplates = async (params: {
  accountType: 'client' | 'partner';
  accountId: string;
  assignedPublicIds: string[];
  analyticsReportingMode?: Genie2ReportingMode | null;
}): Promise<UnassignResponse> => {
  const { accountType, accountId, assignedPublicIds, analyticsReportingMode } = params;
  const { data, status } = await customFetch.post<unknown>(
    `/authenticated/staff/${accountType}/${accountId}/analytics/report-templates/unassign`,
    JSON.stringify({ assignedPublicIds }),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      params: staffConfigModeQueryParams(analyticsReportingMode),
    },
  );

  if (status >= 400) {
    throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
  }
  return data as UnassignResponse;
};

export function useAssignStaffClientReportTemplatesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignStaffClientReportTemplates,
    mutationKey: ['staff', 'client-report-templates', 'assign'],
    onSettled: (_, __, variables) => {
      const accountId = Number(variables.accountId);
      queryClient.invalidateQueries({
        queryKey: staffClientReportTemplatesAccountQueryKey(
          variables.accountType,
          variables.accountId,
        ),
      });
      invalidateClientAnalyticsQueries(queryClient, accountId, variables.accountType);
    },
  });
}

export function useUnassignStaffClientReportTemplatesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unassignStaffClientReportTemplates,
    mutationKey: ['staff', 'client-report-templates', 'unassign'],
    onSettled: (_, __, variables) => {
      const accountId = Number(variables.accountId);
      queryClient.invalidateQueries({
        queryKey: staffClientReportTemplatesAccountQueryKey(
          variables.accountType,
          variables.accountId,
        ),
      });
      invalidateClientAnalyticsQueries(queryClient, accountId, variables.accountType);
    },
  });
}
